/* eslint-disable */
import { createFieldData, detectStyles, findSDKScript, getFormElement, getQueryParams, sentryLog } from "./helpers";
import type { DetectedStylesOutput } from "./helpers";
import type { FieldCardType, FieldMessage, FieldName } from "netvalve-types";
import { handleCardType, handleInitiated, handleTokenization, handleValidation } from "./messageHandlers";
import { InputStyles } from "./types";



let cachedDetectedStyles: DetectedStylesOutput;
let cachedInvalidStyles: DetectedStylesOutput;
let cachedPlaceholderStyles: DetectedStylesOutput;
const thisScript = findSDKScript();

/**
 * Custom HTML element that manages the tokenization of payment card fields.
 * Acts as a container and coordinator for card number, CVV, and expiry field components.
 * 
 * Key responsibilities:
 * - Manages form submission and interception
 * - Handles field tokenization state
 * - Coordinates field validation
 * - Manages styling of child components
 * - Processes cross-field communication (e.g., card type changes)
 * 
 * @fires CustomEvent#cardTypeChange - When card type is detected/changed - used by the cvv field to update its styles
 * @fires CustomEvent#inheritableStylesReady - When styles are ready to be inherited by child components
 * Public properties:
 * @property {string | null} paymentToken - Token used for payment processing
 * @property {string | null} jwtToken - JWT for authentication
 * @property {HTMLFormElement | null} form - Reference to the payment form element
 * @property {Set<FieldName>} tokenizedFields - State maintained for the set of fields that have been tokenized
 * @property {Set<FieldName>} initializedFields - State maintained for the set of fields that have been initialized
 * @property {boolean} interceptFormSubmission - Whether to intercept form submission (default: true)
 * @property {(message: FieldMessage) => void} onValidate - Callback for field validation events
 * @property {(paymentTkn: string) => void} onSubmitPayment - Callback for form submission
 * @property {(message: FieldMessage) => void} onTokenization - Callback for field tokenization events
 */
export class NetvalveTokenFieldsContainer extends HTMLElement {
    public paymentToken: string | null;
    public jwtToken: string | null;
    public formSelector: string | null = null;
    public form: HTMLFormElement | null = null;
    public submitButtonId: string | null = null;
    public ready: boolean = false;
    public tokenizedFields: Set<FieldName> = new Set();
    public initializedFields: Set<FieldName> = new Set();
    public defaultIframeHeight: string = '46px';
    public onValidate: ((message: FieldMessage) => void) | null = null;
    public onSubmitPayment: ((paymentTkn: string) => void) | null = null;
    public onTokenization: ((message: FieldMessage) => void) | null = null;
    public interceptFormSubmission: boolean = true;
    public showCardLogo: boolean = true;
    public font: string | null = null;
    public hidePlaceholder : boolean = false;
    public placeholder : string | null = null;
    private _inputStyles: string | null = '';
    private _placeholderStyles: string | null | InputStyles = '';
    private _invalidStyles: string | null | InputStyles = '';
    private pendingEncryptions: Set<FieldName> = new Set();
    private _cardType: FieldCardType | null = null;
    private formHandlersConfigured: boolean = false;
    private isSubmitting = false;
    
    /**
     * Initializes a new instance of the NetvalveTokenFieldsContainer.
     * Extracts payment and JWT tokens from the SDK script's URL parameters.
     * @throws {Error} If the Netvalve Token Fields SDK script cannot be found in the DOM
     */
    constructor() {
        super();
        if(!thisScript) throw new Error('Netvalve Token Fields SDK script not found');
        const scriptSrc = thisScript.getAttribute('src')!;
        const { paymentToken, jwtToken } = getQueryParams(scriptSrc);
        this.paymentToken = paymentToken;
        this.jwtToken = jwtToken;
    }

    get invalidStyles() {
        return this._invalidStyles;
    }
    
    set invalidStyles(value: string | null | InputStyles) {
        this._invalidStyles = value;
    }

    get inputStyles() {
        return this._inputStyles;
    }
    
    set inputStyles(value: string | null) {
        this._inputStyles = value;
    }

    get placeholderStyles() {
        return this._placeholderStyles;
    }
    
    set placeholderStyles(value: string | null | InputStyles) {
        this._placeholderStyles = value;
    }

    get cardType() {
        return this._cardType;
    }

    /**
     * Lifecycle method called when the element is connected to the DOM.
     * Sets up the component's form selector, intercepts form submission, and initializes the component.
     */
    connectedCallback() {
        if (this.getAttribute('formId')) this.formSelector = `#${this.getAttribute('formId')}`;
        else if (!this.formSelector) this.formSelector = this.getAttribute('formSelector');
        if (this.getAttribute('interceptFormSubmit') === 'false') this.interceptFormSubmission = false;
        if (this.getAttribute('showCardLogo') === 'false') this.showCardLogo = false;
        if (this.getAttribute('hidePlaceholder') === 'true') this.showCardLogo = true;
        if (this.getForm() || document.readyState === 'complete') this.init();
        else document.addEventListener('DOMContentLoaded', () => this.init());
        this.defaultIframeHeight = this.getAttribute('defaultIframeHeight') || '46px';
    }

    /**
     * Retrieves the form element associated with this component.
     * If the form is already cached, it returns the cached form.
     * Otherwise, it attempts to find the form using the formSelector.
     * @returns {HTMLFormElement} The form element.
     * @throws Will throw an error if the form element is not found.
     */
    public getForm(){
        if (this.form) return this.form;
        const el = getFormElement(this.formSelector, this);
        if (!el) throw new Error('Form element not found');
        this.form = el;
        console.log('---FOUND FORM INSIDE NETVALVE-TOKENFIELDS ---', this.form)
        return this.form;
    }

    /**
     * Initializes the component by setting up necessary configurations and event listeners.
     * It inserts the payment token, retrieves the form, dispatches styles, and listens for frame messages.
     * If form handlers are not configured and form submission interception is enabled, it intercepts form submission.
     */
    private init(){
        console.log('---Parent is initializing---')
        this.insertpaymentToken();
        this.getForm();
        this.dispatchInheritableStyles();
        this.listenForFrameMessages();
        if(!this.formHandlersConfigured && this.interceptFormSubmission) this.interceptFormSubmit();
    }

    /**
     * Inserts a hidden input field with the payment token into the form.
     * This method assumes the form element exists and appends the input directly.
     */
    private insertpaymentToken(){
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.style.height = '1px';
        hiddenInput.name = 'paymentToken';
        hiddenInput.value = this.paymentToken!;
        this.getForm()!.appendChild(hiddenInput);  // Do we need to check for existence of form element?
    }

    /**
     * Dispatches inheritable styles to child components.
     * It collects styles from attributes and dispatches an event to notify that styles are ready.
     * Styles are cached for performance optimization.
     */
    public dispatchInheritableStyles(){
        this.inputStyles = this.getAttribute('style');

        if (!this.invalidStyles) this.invalidStyles = this.getAttribute('invalidStyles'); // collect so children can access it
        if (!this.placeholderStyles) this.placeholderStyles = this.getAttribute('placeholderStyles'); // collect so children can access it
        if (!this.font) this.font = this.getAttribute('font'); // collect so children can access it
        console.log('----- Dispatching inheritableStylesReady event ----')
        if (this.inputStyles) {
            requestAnimationFrame(() => {
                cachedDetectedStyles = detectStyles({
                    sourceElement: this,
                    elementStyles: this.inputStyles || '',
                    referenceContainer: this.form!
                });  // perform cached detected styles here 
                this.setAttribute('style', '');
                this.ready = true;
                // Notify iframe fields that the parent's styles are ready
                window.dispatchEvent(new CustomEvent('inheritableStylesReady', { bubbles: true }));
            });
        } else {
            this.ready = true;
            window.dispatchEvent(new CustomEvent('inheritableStylesReady', { bubbles: true }));
        }
    }

    /**
     * Updates the set of tokenized fields by adding or removing specified fields.
     * @param {FieldName | null} fieldToAdd - The field to add to the tokenized set.
     * @param {FieldName} [fieldToRemove] - The field to remove from the tokenized set.
     */
    private setTokenizedFields(fieldToAdd: FieldName | null, fieldToRemove?: FieldName){
        if (fieldToRemove) this.tokenizedFields.delete(fieldToRemove);
        if (fieldToAdd) this.tokenizedFields.add(fieldToAdd);
    }

    /**
     * Removes a field from the set of pending encryptions.
     * @param {FieldName} field - The field to remove from pending encryptions.
     */
    private deletePendingEncryption(field: FieldName){
        this.pendingEncryptions.delete(field);
    }

    /**
     * Returns the count of tokenized fields.
     * @returns {number} The number of tokenized fields.
     */
    private getTokenizedFieldsCount(): number {
        return this.tokenizedFields.size;
    }

    /**
     * Sets the card type and dispatches a 'cardTypeChange' event if the card type changes.
     * @param {FieldCardType | null} value - The new card type value.
     */
    private setCardType(value: FieldCardType | null) {
        if (this._cardType !== value) {
            this._cardType = value;
            // Dispatch this event for the cvv field
            this.dispatchEvent(new CustomEvent('cardTypeChange', { 
                detail: { cardType: value },
                bubbles: true 
            }));
        }
    }

    /**
     * Validates all fields within the component.
     * @returns {Promise<boolean>} A promise that resolves to true if all fields are valid, otherwise false.
     */
    public async validateAll() {
        const cardNumber = this.querySelector('netvalve-cardnumber') as NetvalveCardNumber;
        const cvv = this.querySelector('netvalve-cvv') as NetvalveCVV;
        const expiry = this.querySelector('netvalve-expiry') as NetvalveExpiry;

        const results = await Promise.all([
            cardNumber?.validate(),
            cvv?.validate(),
            expiry?.validate()
        ]);

        console.log('--- VALIDATION RESULTS ---', results)

        // returns false if any field's success is false
        return results.every(result => result?.success ?? false);
    }

    /**
     * Handles form submission by validating fields and ensuring encryption is complete.
     * If a callback is provided, it is called with the payment token upon successful submission.
     * @param {(paymentTkn: string) => void} [callback] - Optional callback to execute on successful submission.
     * @returns {Promise<boolean>} A promise that resolves to true if submission is successful, otherwise false.
     */
    private async handleFormSubmit(callback?: (paymentTkn: string) => void){
        // validate the fields
        console.log('--- FORM SUBMITTED, PERFORMING VALIDATION ---');
        try {
            const allFieldsValid = await this.validateAll();
            const allFieldsEncrypted = this.getTokenizedFieldsCount() === 3;
            const hasEncryptionsPending = this.pendingEncryptions.size > 0;

            if (!allFieldsValid) {
                console.log('--- VALIDATION ERRORS ---', { allFieldsValid, allFieldsEncrypted, hasEncryptionsPending }); // validation errors would be triggered already
                return false;
            }
            if (!allFieldsEncrypted && !hasEncryptionsPending) {
                console.error('Fields are not encrypted', { allFieldsEncrypted, hasEncryptionsPending });
                return false;
            }

            //if validation passed, but not all fields have been encrypted
            if (hasEncryptionsPending) {

                // this.displayPendingEncryption();
                // OR trigger event for merchant to be aware of the pending encryptions after submit
                console.log('--- WAITING FOR PENDING ENCRYPTIONS ---', this.pendingEncryptions)
                await new Promise<void>((resolve, reject) => {
                    const startTime = Date.now();
                    const TIMEOUT = 5000; // 5 seconds
                    let interval: NodeJS.Timeout;

                    const checkForPending = () => {
                        if (this.pendingEncryptions.size === 0) {
                            clearInterval(interval);
                            resolve();
                        }
                        
                        const elapsedTime = Date.now() - startTime;
                        if (elapsedTime >= TIMEOUT) {
                            clearInterval(interval);
                            sentryLog({
                                name: 'PENDING_ENCRYPTION_TIMEOUT',
                                msg: 'Upon submit, a pending field encryption timeout occurred',
                                extra: {
                                    pendingFields: Array.from(this.pendingEncryptions),
                                    tokenizedFields: Array.from(this.tokenizedFields),
                                }
                            });
                            reject('Field encryption timed out');
                        }
                    };
                    
                    interval = setInterval(checkForPending, 100); 
                });
            }

            if (callback) callback(this.paymentToken!);
            else this.form!.submit();

            return true;
            
        } catch(error) {
            // This will only catch the timeout error
            console.error('Form submission failed:', error);
            return false;
        }   
    }

    /**
     * Tokenizes fields and handles form submission.
     * Empty callback added to prevent the form submission, and only run validation and tokenization
     * @returns {Promise<string | null>} A promise that resolves to the payment token if successful, otherwise null.
     */
    public async tokenizeFields(){
        const result = await this.handleFormSubmit(( )=>{});
        return result ? this.paymentToken : null;
    }

    /**
     * Checks if all fields have been tokenized.
     * @returns {boolean} True if the number of tokenized fields is equal to 3, otherwise false.
     */
    public allFieldsTokenized(){
        return this.getTokenizedFieldsCount() === 3;
    }

    /**
     * Checks if there are pending encryptions.
     * @returns {boolean} True if there are pending encryptions, otherwise false.
     */
    public isTokenizing(){
        return this.pendingEncryptions.size > 0;
    }

    /**
     * Controls the form submission process, preventing default submission and handling it manually.
     * @param {Event} event - The form submission event.
     * @param {(token: string) => void} [callback] - Optional callback to execute on successful submission.
     */
    private async controlSubmit(event: Event, callback?: (token: string) => void) {
        if (this.isSubmitting) return; // Prevent double submission
        
        event.preventDefault();
        event.stopPropagation();
        
        this.isSubmitting = true;
        try {
            await this.handleFormSubmit(callback);
        } finally {
            this.isSubmitting = false;
        }
    }

    /**
     * Intercepts form submission to handle it programmatically.
     * Configures event listeners for form submission and button clicks.
     * @param {(paymentTkn: string) => void} [submitCallback] - Optional callback to execute on successful submission.
     * @param {string} [submitButtonId] - Optional ID of the submit button to listen for clicks.
     * @throws Will throw an error if the form element is not found.
     */
    public interceptFormSubmit(submitCallback?: (paymentTkn: string) => void, submitButtonId?: string){

        if (!this.getForm()) throw new Error('Form element not found in interceptFormSubmit');
        this.formHandlersConfigured = true;
        const callback = submitCallback || this.onSubmitPayment;
        const buttonId = submitButtonId || this.submitButtonId;

        console.log('---INTERCEPTING FORM SUBMIT---', { onSubmitPaymentCallback: !!callback, submitButtonId: !!buttonId })
    
        // If using callback approach (recommended)
        if (callback) {
            // Handle button click if ID provided
            if (buttonId) {
                this.form!.addEventListener('submit', e => this.controlSubmit(e, callback), true);
                
                const submitBtn = document.getElementById(buttonId);
                submitBtn?.addEventListener('click', e => this.controlSubmit(e, callback), true);
            } else {
                // a button with 'submit' type, block form submission
                let submitBtn = this.form?.querySelector('button[type="submit"]') || this.form?.querySelector('input[type="submit"]');
                if (submitBtn) {
                    submitBtn.addEventListener('click', e => this.controlSubmit(e, callback), true);
                }
                this.form!.addEventListener('submit', e => this.controlSubmit(e, callback), true);
            }
            return;
        }

            // No callback provided - this is the case where we just want to block normal form submission
            // and handle it manually through form.submit()
            let submitBtn = buttonId ? document.getElementById(buttonId) : this.form?.querySelector('button[type="submit"]') || this.form?.querySelector('input[type="submit"]');
            if (submitBtn) {
                submitBtn.addEventListener('click', e => this.controlSubmit(e), true);
            }
            this.form!.addEventListener('submit', e => this.controlSubmit(e), true);
    }

    /**
     * Listens for messages from iframes and handles them based on their type.
     * It processes messages related to card type, encryption, initialization, validation, and tokenization.
     */
    private listenForFrameMessages(){
        window.addEventListener('message', (event: MessageEvent) => {
            if (event.origin !== import.meta.env?.VITE_APP_TOKEN_FIELD_IFRAME_URL) return;
            console.log(`----MESSAGE RECEIVED FROM TOKEN FIELD ${event.data.fieldName} IFRAME----`, event.data);
            const message = event.data as FieldMessage;
            if (message.type === 'cardType') handleCardType(message, this._cardType, (value) => this.setCardType(value));
            else if (message.type === 'encryptionStart') this.pendingEncryptions.add(message.fieldName);
            else if (message.type === 'initiated') handleInitiated(message, this.initializedFields);
            else if (message.type === 'validation' ) handleValidation(message, this.tokenizedFields, this.setTokenizedFields.bind(this), this.onValidate);
            else if (message.type === 'tokenization') handleTokenization(message, this.tokenizedFields, this.setTokenizedFields.bind(this), this.deletePendingEncryption.bind(this), this.onTokenization);
        })
    }

}

/**
 * Base class for tokenizer field components (card number, CVV, expiry) - they each inherit from this class.
 * Provides common functionality for field initialization and iframe management.
 * 
 * Key responsibilities:
 * - Manages parent-child relationship with NetvalveTokenFieldsContainer
 * - Handles iframe creation and style inheritance
 * - Processes card type changes
 * - Manages field validation
 * 
 * @property {NetvalveTokenFieldsContainer | null} parent - Reference to parent container element
 * @property {FieldName | null} type - Type of field ('cardNumber', 'cardCvv', or 'cardExpiry')
 * @property {HTMLIFrameElement | null} iframe - Reference to the secure iframe element
 * @property {string} savedStyles - Cached styles from initial element attributes
 * 
 * @listens CustomEvent#inheritableStylesReady - When parent container's styles are ready to be inherited
 * @listens CustomEvent#cardTypeChange - When card type changes (for updating CVV field validation)
 * @listens CustomEvent#stylesUpdated - When parent container's styles are updated
 */
export class NetvalveTokenizerBase extends HTMLElement {

    parent: NetvalveTokenFieldsContainer | null = null;
    type: FieldName | null = null;
    iframe: HTMLIFrameElement | null = null;
    private savedStyles: string = '';
    private placeholder: string | null = null;

    constructor() {
        super();
    }

    /**
    * Lifecycle callback invoked when the element is added to the document's DOM.
    * Handles initial setup and parent-child relationship establishment.
    * 
    * The method:
    * 1. Saves and clears initial element styles
    * 2. Sets up listener for inheritable styles from parent
    * 3. Attempts to find and connect to parent container
    * 4. Initializes the component if parent is ready
    * 
    * @listens inheritableStylesReady - When parent container's styles are ready
    */
    connectedCallback() {

        this.savedStyles = this.getAttribute('style') || this.getAttribute('savedstyles') || '';
        this.setAttribute('savedstyles', this.savedStyles);
        this.setAttribute('style', '');
        this.placeholder = this.getAttribute('placeholder');

        window.addEventListener('inheritableStylesReady', () => {
            console.log('---INHERITABLE STYLES REACEIVED---', this.type)
            // Try to find parent again after initiated event
            this.parent = this.closest('netvalve-tokenfields') as NetvalveTokenFieldsContainer;
            if (this.parent) this.initialize();
            else console.warn('inheritableStylesReady Event received but there is no parent, cannot innitialized')
        });

        const parent = this.closest('netvalve-tokenfields') as NetvalveTokenFieldsContainer;
        if (parent) {
            this.parent = parent;
            if (parent.ready) this.initialize();
            else console.warn('The parent does not exist or is not yet ready. Waiting for the event')
        }
        // if parent does not exist or is not ready, it must wait for inheritableStylesReady event.

    }

    /**
     * Sets up the tokenizer field component after parent connection is established.
     * 
     * The method:
     * 1. Verifies parent container exists
     * 2. Sets up card type change listener
     * 3. Sets up style update listener
     * 4. Creates iframe and applies initial styles
     * 
     * @private
     * @listens cardTypeChange - When card type is detected/changed
     * @listens stylesUpdated - When parent container's styles are updated
     */
    private initialize() {
        
        if (!this.parent) return console.warn('The parent netvalve-tokenfields element is not available, unable initialize'); 
        if (this.iframe) return this.postStylesToIframe(); // if iframe exists, supply styles

        // Set up card type listener
        this.parent.addEventListener('cardTypeChange', ((event: Event) => {
            const customEvent = event as CustomEvent;
            this.onCardTypeChange(customEvent.detail.cardType);
        }) as EventListener);

        // Set up style update listener in case styles are received later
        this.parent.addEventListener('stylesUpdated', () => {
            this.postStylesToIframe();
        });

        this.createIframeAndPostStyles();
    }

    /**
     * Validates the current field value by communicating with the iframe.
     * 
     * @returns {Promise<FieldMessage | null>} Resolves with validation message or null if validation fails
     * @fires MessageEvent - Posts validation request to iframe. The tokenizefield app will validate the input and respond with a validation message.
     * @listens MessageEvent - Waits for validation response from the iframe
     */
    public validate(): Promise<FieldMessage | null> {
        return new Promise((resolve) => {
            const iframe = this.querySelector('iframe');
            if (!iframe) {
                resolve(null);
                return;
            }

            // Create one-time listener for validation response
            const handleValidationResponse = (event: MessageEvent) => {
                if (event.origin !== import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL) return;
                const message = event.data as FieldMessage;
                
                if (message.type === 'validation' && message.fieldName === this.type) {
                    // Remove the listener once we get our response
                    window.removeEventListener('message', handleValidationResponse);
                    resolve(message);
                }
            };

            window.addEventListener('message', handleValidationResponse);
            
            // Send validation request to iframe
            iframe.contentWindow?.postMessage(createFieldData({
                fieldName: this.type!,
                paymentToken: this.parent?.paymentToken!,
                jwtToken: this.parent?.jwtToken!,
                validate: true
            }), `${import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL}`);
        });
    }

    /**
     * Handles card type changes by updating the CVV field validation rules.
     * Only applies to CVV fields as validation rules depend on card type.
     * 
     * @private
     * @param {FieldCardType | null} [cardType] - Detected card type information
     * @fires MessageEvent - Posts card type data to iframe if field is CVV
     */
    private onCardTypeChange(cardType?: FieldCardType | null) {
        const iframe = this.querySelector('iframe');
        if (!iframe || !cardType || this.type !== 'cardCvv') return; // only send card type for cvv field

        iframe.contentWindow?.postMessage(createFieldData({
            fieldName: this.type!,
            typeData: cardType,
            jwtToken: this.parent?.jwtToken!,
            paymentToken: this.parent?.paymentToken!,
        }), `${import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL}`);
    }


    /**
     * Creates and configures the secure iframe element for the field.
     * Sets up initial styling and dimensions.
     * 
     * @private
     * @fires load - When iframe is loaded, triggers initial style posting
     */
    private createIframeAndPostStyles() {
    
        if (!this.iframe) {
            this.iframe = document.createElement('iframe');
            this.iframe.src = new URL(import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL).href;
            this.iframe.style.width = '100%';
            this.iframe.style.display = 'block';
            this.iframe.style.border = 'none';
            this.iframe.style.height = this.parent?.defaultIframeHeight || '46px';
            
            this.appendChild(this.iframe);
            // Initial styles will be sent once iframe is loaded
            this.iframe.onload = () => {
                this.postStylesToIframe();
            };
        }
    }
 

    /**
     * Posts computed styles to the field iframe.
     * Handles style inheritance, custom styles, and special states (invalid, placeholder).
     * 
     * The method:
     * 1. Detects and caches styles for performance
     * 2. Computes styles for different states (invalid, placeholder etc)
     * 3. Updates iframe dimensions
     * 4. Posts style data to iframe - so the input is able to inherit the styles
     * 
     * @private
     * @throws {Error} If parent or iframe is not available
     * @fires MessageEvent - Posts style data to iframe
     */
    postStylesToIframe() {

        try {

            if (!this.iframe || !this.parent) throw new Error('Unable to post styles. Either the parent or the iframe is not present');

            const hasCustomStyles = this.savedStyles;
            const disableStylesDetector = this.getAttribute('disableStylesDetector');
            const invalidStyles = this.parent?.invalidStyles;
            const placeHolderStyles = this.parent?.placeholderStyles;
            const inheritedStyles = this.parent?.inputStyles;
            const hasCustomFont = this.parent?.font;

            console.log(`---POSTING STYLES TO IFRAME: ${this.type}---`, { hasCustomStyles, disableStylesDetector, invalidStyles, placeHolderStyles, inheritedStyles, hasCustomFont });


            let detectedStyles: DetectedStylesOutput, detectedInvalidStyles: DetectedStylesOutput | undefined, detectedPlaceholderStyles: DetectedStylesOutput | undefined;
            
            
            
            const shouldCalculateStyles = (
                !cachedDetectedStyles ||  // No cached styles exist yet
                hasCustomStyles ||        // Custom styles are defined on the element
                (invalidStyles && !cachedInvalidStyles) ||         // Invalid styles exist but aren't cached
                (placeHolderStyles && !cachedPlaceholderStyles)   // Placeholder styles exist but aren't cached
            );
            if (shouldCalculateStyles) {
                // First detect styles without overrides - no custom styles
                const newDetectedStyles = detectStyles({
                    sourceElement: this,
                    elementStyles: this.savedStyles,
                    inheritedStyles: inheritedStyles || ''
                });
                
                // If there are invalid styles, detect those too
                if (invalidStyles) {
                    if (!cachedInvalidStyles || hasCustomStyles) {
                        const overrideStyleString = typeof invalidStyles === 'string' 
                        ? invalidStyles // styles set in html
                        : Object.entries(invalidStyles) // styles set in js config
                            .map(([property, value]) => `${property}: ${value};`)
                            .join(' ');

                        detectedInvalidStyles = detectStyles({
                            sourceElement: this,
                            elementStyles: this.savedStyles,
                            inheritedStyles: inheritedStyles || '',
                            overrideStyles: overrideStyleString
                        });

                        // Cache invalid styles if no custom styles
                        if (!hasCustomStyles) cachedInvalidStyles = detectedInvalidStyles;
                    } else {
                        detectedInvalidStyles = cachedInvalidStyles;
                    }
                }

                // If there are placeholder styles, detect those too
                if (placeHolderStyles) {
                    if (!cachedPlaceholderStyles || hasCustomStyles) {
                        const overrideStyleString = typeof placeHolderStyles === 'string' 
                        ? placeHolderStyles // styles set in html
                        : Object.entries(placeHolderStyles) // styles set in js config
                            .map(([property, value]) => `${property}: ${value};`)
                            .join(' ');

                        detectedPlaceholderStyles = detectStyles({
                            sourceElement: this,
                            elementStyles: this.savedStyles,
                            inheritedStyles: inheritedStyles || '',
                            overrideStyles: overrideStyleString,
                            type: 'placeholder'
                        });

                        // Cache invalid styles if no custom styles
                        if (!hasCustomStyles) cachedPlaceholderStyles = detectedPlaceholderStyles;
                    } else {
                        detectedPlaceholderStyles = cachedPlaceholderStyles;
                    }
                }
            
                if (cachedDetectedStyles)  detectedStyles = { ...cachedDetectedStyles, ...newDetectedStyles };
                else detectedStyles = newDetectedStyles;
        
                // Cache default styles if no custom styles exist
                if (!hasCustomStyles) cachedDetectedStyles = newDetectedStyles;
            } else {
                // Use cached styles
                detectedStyles = cachedDetectedStyles;
                detectedInvalidStyles = cachedInvalidStyles;
                detectedPlaceholderStyles = cachedPlaceholderStyles;
            }

            this.iframe!.style.height = detectedStyles.inputHeight;

            // Enable autofill inside iframe
            this.iframe!.allow = "autofill";

            // Post updated styles to iframe
            this.iframe!.contentWindow?.postMessage(createFieldData({ 
                styles: disableStylesDetector ? {} : detectedStyles.inputStyles,
                invalidStyles: detectedInvalidStyles?.inputStyles,
                placeholderStyles: detectedPlaceholderStyles?.inputStyles,
                jwtToken: this.parent!.jwtToken!,
                paymentToken: this.parent!.paymentToken!,
                fieldName: this.type!,
                showCardLogo: this.parent!.showCardLogo!,
                font: this.parent!.font!,
                hidePlaceholder: this.parent!.hidePlaceholder!,
                placeholder: this.placeholder!,
            }), `${import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL}`);

        } catch(error) {
      
            sentryLog({
                name: 'STYLE',
                msg: 'Error posting styles to iframe',
                error: error as Error,
                tags: {
                    fieldType: this.type || 'unknown',
                },
                extra: {
                    fieldType: this.type,
                    paymentToken: this.parent?.paymentToken || 'unknown',
                    hasIframe: !!this.iframe,
                    hasParentElement: !!this.parent,
                    hasCustomStyles: !!this.savedStyles,
                    hasInvalidStyles: !!this.parent?.invalidStyles,
                    hasPlaceholderStyles: !!this.parent?.placeholderStyles,
                    hasInheritedStyles: !!this.parent?.inputStyles,
                    iframeUrl: import.meta.env.VITE_APP_TOKEN_FIELD_IFRAME_URL,
                }
            });
            
        }
    }

    
}

// Define the custom card input elements
export class NetvalveCardNumber extends NetvalveTokenizerBase {

    connectedCallback() {
        super.connectedCallback();
        this.type = 'cardNumber';
        this.setAttribute('type', 'cardNumber');
    }
}

export class NetvalveCVV extends NetvalveTokenizerBase {
    connectedCallback() {
        super.connectedCallback();
        this.type = 'cardCvv';
        this.setAttribute('type', 'cardCvv');
    }
}

export class NetvalveExpiry extends NetvalveTokenizerBase {
    connectedCallback() {
        super.connectedCallback();
        this.type = 'cardExpiry';
        this.setAttribute('type', 'cardExpiry');
    }
}

